import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';
import authService from '../services/authService';
import { jsPDF } from 'jspdf';

const FireMarshalDashboard = () => {
  const [activeTab, setActiveTab] = useState('reports');
  const [missingStudents, setMissingStudents] = useState({});
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false); // Separate loading state for events list
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [allDates, setAllDates] = useState([]);
  const [currentEventId, setCurrentEventId] = useState(null);
  const [events, setEvents] = useState([]);
  const [startingEvent, setStartingEvent] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [timer, setTimer] = useState(0);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [viewingEventDetails, setViewingEventDetails] = useState(false);
  const [drillStartTime, setDrillStartTime] = useState(null); // Track when drill started
  const [formsStatus, setFormsStatus] = useState([]); // Forms with submission status
  const [selectedEventId, setSelectedEventId] = useState(null); // Which event's details we're viewing
  const [selectedCompletedEvent, setSelectedCompletedEvent] = useState(null); // Selected previous drill
  const [selectedCompletedEventDetails, setSelectedCompletedEventDetails] = useState(null); // Details of selected previous drill
  const [loadingDetails, setLoadingDetails] = useState(false); // Loading indicator for details
  const [mainView, setMainView] = useState('active'); // 'active' or 'previous'

  const currentUser = authService.getCurrentUser();

  // Initial load - only run once on mount
  useEffect(() => {
    console.log("Component mounted, checking for saved state...");
    
    const savedEventId = localStorage.getItem('activeFireDrillEventId');
    const savedStartTime = localStorage.getItem('firedrillStartTime');
    const wasViewingDetails = localStorage.getItem('firedrillViewingDetails') === 'true';
    
    // If we were viewing an event before refresh, restore that
    if (savedEventId && wasViewingDetails) {
      console.log(`🔥 Restoring event details for ${savedEventId} after page refresh`);
      setCurrentEventId(savedEventId);
      if (savedStartTime) {
        setDrillStartTime(parseInt(savedStartTime));
      }
      loadEventDetails(savedEventId);
      setViewingEventDetails(true);
    } else {
      // Otherwise load list view
      console.log("Loading global event list");
      loadData();
    }
  }, []); // Run only once on mount

  // Auto-refresh based on viewing mode
  useEffect(() => {
    const interval = setInterval(() => {
      if (viewingEventDetails && currentEventId) {
        // If viewing an event, refresh only that event's details
        console.log(`🔄 Auto-refreshing event details for ${currentEventId}`);
        loadEventDetails(currentEventId);
      } else if (!viewingEventDetails) {
        // If viewing list, refresh global data
        console.log("🔄 Auto-refreshing global event list");
        loadData();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [viewingEventDetails, currentEventId]);

  // Load events periodically to check if active drill is still going
  useEffect(() => {
    // Load events every 10 seconds to verify drill status
    const interval = setInterval(() => {
      loadEvents();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  // Track viewing state in localStorage
  useEffect(() => {
    if (currentEventId && viewingEventDetails) {
      localStorage.setItem('firedrillViewingDetails', 'true');
    } else {
      localStorage.removeItem('firedrillViewingDetails');
    }
  }, [viewingEventDetails, currentEventId]);

  // Timer effect for active event
  useEffect(() => {
    // First, always check localStorage to restore if needed
    const savedEventId = localStorage.getItem('activeFireDrillEventId');
    const savedStartTime = localStorage.getItem('firedrillStartTime');
    
    if (savedEventId && savedStartTime && !currentEventId) {
      // Restore from localStorage
      setCurrentEventId(savedEventId);
      setDrillStartTime(parseInt(savedStartTime));
      return; // Exit early to let the effect run again with restored values
    }
    
    // Now check if we have both values set
    if (!currentEventId || !drillStartTime) {
      setTimer(0);
      localStorage.removeItem('activeFireDrillEventId');
      localStorage.removeItem('firedrillStartTime');
      return;
    }

    // Calculate elapsed time from start time
    const calculateElapsed = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - drillStartTime) / 1000);
      setTimer(elapsed);
    };

    // Calculate immediately
    calculateElapsed();

    // Update every second
    const interval = setInterval(calculateElapsed, 1000);

    return () => clearInterval(interval);
  }, [currentEventId, drillStartTime]);

  // 🔥 WebSocket connection for real-time fire drill updates
  useEffect(() => {
    if (!currentEventId || !viewingEventDetails) {
      console.log("No active event or not viewing details, skipping WebSocket connection");
      return;
    }

    // Build WebSocket URL from buildApiUrl
    const apiUrl = buildApiUrl('/').replace(/\/$/, ''); // Remove trailing slash
    const protocol = apiUrl.includes('https') ? 'wss:' : 'ws:';
    const host = apiUrl.replace(/^https?:\/\//, '').replace(/\/$/, ''); // Extract host
    const wsUrl = `${protocol}//${host}/ws/fire-drill/${currentEventId}`;
    
    console.log(`🔥 Connecting to fire drill WebSocket: ${wsUrl}`);
    
    let ws;
    let reconnectTimeout;
    let isConnected = false;
    
    const connectWebSocket = () => {
      try {
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          isConnected = true;
          console.log(`✅ Fire drill WebSocket connected for event ${currentEventId}`);
          // Send initial connection message
          ws.send(JSON.stringify({ type: 'connect', event_id: currentEventId }));
        };
        
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log(`📡 Fire drill update received:`, message);
            
            if (message.type === 'report_update') {
              // Update the forms status with the new report data
              const updatedReport = message.report;
              console.log(`🔄 Updating form ${updatedReport.form} with:`, updatedReport);
              
              setFormsStatus(prevStatus => {
                const newStatus = prevStatus.map(form => {
                  if (form.form === updatedReport.form) {
                    console.log(`📝 Form ${form.form} before:`, form);
                    const updated = {
                      form: updatedReport.form,
                      submitted: updatedReport.submitted || true,
                      teacher_name: updatedReport.teacher_name,
                      teacher_email: updatedReport.teacher_email,
                      total_students: updatedReport.total_students,
                      present_count: updatedReport.present_count,
                      missing_count: updatedReport.missing_count,
                      unknown_count: updatedReport.unknown_count,
                      submitted_at: updatedReport.submitted_at,
                      time_spent_seconds: updatedReport.time_spent_seconds
                    };
                    console.log(`📝 Form ${form.form} after:`, updated);
                    return updated;
                  }
                  return form;
                });
                console.log(`📋 Updated formsStatus:`, newStatus);
                return newStatus;
              });
              
              // Also update missing students if provided
              if (updatedReport.missing_students && updatedReport.missing_students.length > 0) {
                const today = new Date().toISOString().split('T')[0];
                setMissingStudents(prev => ({
                  ...prev,
                  [today]: updatedReport.missing_students
                }));
              }
            }
          } catch (err) {
            console.error('Error parsing WebSocket message:', err);
          }
        };
        
        ws.onerror = (error) => {
          isConnected = false;
          console.error(`❌ Fire drill WebSocket error:`, error);
        };
        
        ws.onclose = () => {
          isConnected = false;
          console.log(`🔌 Fire drill WebSocket disconnected for event ${currentEventId}`);
          // Attempt to reconnect after 5 seconds
          if (currentEventId && viewingEventDetails) {
            reconnectTimeout = setTimeout(connectWebSocket, 5000);
          }
        };
      } catch (err) {
        isConnected = false;
        console.error('Error connecting to fire drill WebSocket:', err);
        if (currentEventId && viewingEventDetails) {
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      }
    };
    
    connectWebSocket();
    
    // Cleanup on unmount or when event changes
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws && isConnected) {
        console.log('🔌 Closing WebSocket');
        ws.close();
      }
    };
  }, [currentEventId, viewingEventDetails]);

  const loadData = async () => {
    // Skip if we're currently viewing event details
    if (viewingEventDetails) {
      console.log("Skipping loadData because viewing event details");
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Fetch missing students with cache-busting
      const missingRes = await fetch(
        buildApiUrl(`/fire-drill/missing-students?t=${Date.now()}`)
      );
      const missingData = await missingRes.json();

      // Fetch reports with cache-busting
      const reportsRes = await fetch(
        buildApiUrl(`/fire-drill/reports?t=${Date.now()}`)
      );
      const reportsData = await reportsRes.json();

      if (missingData.success) {
        setMissingStudents(missingData.grouped_by_date || {});
      }

      if (reportsData.success) {
        setReports(reportsData.grouped_by_date || {});
      }

      // Collect all unique dates
      const dates = new Set([
        ...Object.keys(missingData.grouped_by_date || {}),
        ...Object.keys(reportsData.grouped_by_date || {})
      ]);
      setAllDates(Array.from(dates).sort().reverse());

      // Set first date as selected if none selected
      if (!selectedDate && dates.size > 0) {
        setSelectedDate(Array.from(dates).sort().reverse()[0]);
      }
    } catch (err) {
      console.error('Error loading fire drill data:', err);
      setError('Failed to load fire drill data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const res = await fetch(buildApiUrl(`/fire-drill/events?t=${Date.now()}`));
      const data = await res.json();
      if (data.success) {
        setEvents(data.events || []);
        
        // Check if we have a currently active event
        const activeEventIdFromStorage = localStorage.getItem('activeFireDrillEventId');
        
        if (activeEventIdFromStorage) {
          // We have a drill that should be active - verify it still exists
          const activeEvent = data.events?.find(e => e.event_id === activeEventIdFromStorage);
          
          if (activeEvent) {
            // Event still exists
            setCurrentEvent(activeEvent);
            
            // If status changed to completed, end the drill
            if (activeEvent.status === 'completed') {
              setCurrentEventId(null);
              setCurrentEvent(null);
              setTimer(0);
              setDrillStartTime(null);
              localStorage.removeItem('activeFireDrillEventId');
              localStorage.removeItem('firedrillStartTime');
            }
            // If still active, keep it going (don't clear!)
          }
          // If event doesn't exist in response, keep local state - it might be a network issue
        }
      }
    } catch (err) {
      console.error('Error loading events:', err);
      // On error, don't clear the local state - keep the drill going
    }
  };

  const loadEventDetails = async (eventId) => {
    try {
      setLoadingDetails(true);
      // Don't set loading(true) here - it blocks the whole UI
      
      // Load all data in parallel for speed
      const [eventDetailsRes, reportsRes] = await Promise.all([
        fetch(buildApiUrl(`/fire-drill/event-details/${eventId}?t=${Date.now()}`)),
        fetch(buildApiUrl(`/fire-drill/reports?event_id=${eventId}&t=${Date.now()}`))
      ]);

      const [eventDetailsData, reportsData] = await Promise.all([
        eventDetailsRes.json(),
        reportsRes.json()
      ]);
      
      // Set forms status first (this loads fastest from event-details endpoint)
      if (eventDetailsData.success) {
        setFormsStatus(eventDetailsData.forms || []);
        setSelectedEventId(eventId);
        
        // 🔥 FIX: Extract missing students only from current event's forms
        // Build missing students object from submitted forms that have missing_students
        const currentEventMissing = {};
        const forms = eventDetailsData.forms || [];
        
        console.log(`🔥 DEBUG: Event ${eventId} has ${forms.length} forms`);
        
        forms.forEach(form => {
          const missingCount = form.missing_students ? form.missing_students.length : 0;
          console.log(`   Form ${form.form}: submitted=${form.submitted}, missing=${missingCount}`);
          
          if (form.missing_students && form.missing_students.length > 0) {
            const formKey = form.form;
            currentEventMissing[formKey] = form.missing_students.map(student => ({
              student_id: student.student_id || student,
              student_name: student.student_name || student,
              form: form.form,
              teacher: form.teacher_name,
              teacher_email: form.teacher_email,
              reported_at: form.submitted_at
            }));
          }
        });
        
        console.log(`🔥 DEBUG: Extracted missing students from ${Object.keys(currentEventMissing).length} forms`);
        
        // Set missing students data - only from current event
        setMissingStudents(currentEventMissing);
        
        // 🔥 FIX: Only set reports data from the current event
        if (reportsData.success && reportsData.grouped_by_date) {
          setReports(reportsData.grouped_by_date);
        } else {
          setReports({});
        }
      }

      // Collect all unique dates from reports for this event
      const dates = new Set(Object.keys(reportsData.grouped_by_date || {}));
      setAllDates(Array.from(dates).sort().reverse());
      
      // Set first date as selected
      if (dates.size > 0) {
        setSelectedDate(Array.from(dates).sort().reverse()[0]);
      } else {
        // If no dates, set today's date
        const today = new Date().toISOString().split('T')[0];
        setSelectedDate(today);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading event details:', err);
      setError('Failed to load event details: ' + err.message);
    } finally {
      // Don't set loading(false) here - only loadingDetails
      setLoadingDetails(false);
    }
  };

  const endFireDrill = async () => {
    if (!currentEventId) return;
    
    try {
      const response = await fetch(buildApiUrl('/fire-drill/end-event'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: currentEventId,
          duration: timer  // Send the elapsed time in seconds
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Fire drill event ended! Duration: ${formatDuration(timer)}`);
        setCurrentEventId(null);
        setCurrentEvent(null);
        setTimer(0);
        setDrillStartTime(null);
        // Clear localStorage
        localStorage.removeItem('activeFireDrillEventId');
        localStorage.removeItem('firedrillStartTime');
        setTimeout(() => setSuccessMessage(''), 5000);
        loadEvents();
      } else {
        setError('Failed to end fire drill: ' + data.error);
      }
    } catch (err) {
      console.error('Error ending fire drill:', err);
      setError('Failed to end fire drill: ' + err.message);
    }
  };

  const deleteFireDrillEvent = async (event_id) => {
    if (!window.confirm('Are you sure you want to delete this fire drill event and all its reports? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl('/fire-drill/delete-event'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: event_id
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`🗑️ Fire drill event and ${data.reports_deleted} report(s) deleted successfully`);
        setTimeout(() => setSuccessMessage(''), 5000);
        loadEvents();
      } else {
        setError('Failed to delete fire drill: ' + data.error);
      }
    } catch (err) {
      console.error('Error deleting fire drill:', err);
      setError('Failed to delete fire drill: ' + err.message);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined || seconds === 0) {
      return 'N/A';
    }
    
    seconds = parseInt(seconds);
    if (isNaN(seconds) || seconds < 0) {
      return 'N/A';
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    // Format: show minutes as primary unit
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else if (seconds >= 0) {
      return `${secs}s`;
    }
    
    return 'N/A';
  };

  const calculateEventDuration = (event) => {
    // If duration_seconds is already set, use it
    if (event.duration_seconds && event.duration_seconds > 0) {
      return event.duration_seconds;
    }
    
    // Try to calculate from timestamps
    if (event.started_at && event.completed_at) {
      const startTime = new Date(event.started_at);
      const endTime = new Date(event.completed_at);
      const durationMs = endTime - startTime;
      if (durationMs > 0) {
        return Math.floor(durationMs / 1000); // Convert to seconds
      }
    }
    
    // Fallback: try created_at and completed_at
    if (event.created_at && event.completed_at) {
      const startTime = new Date(event.created_at);
      const endTime = new Date(event.completed_at);
      const durationMs = endTime - startTime;
      if (durationMs > 0) {
        return Math.floor(durationMs / 1000); // Convert to seconds
      }
    }
    
    return null;
  };

  // Show all forms from backend (already filtered to current school)
  const filteredForms = formsStatus;

  const exportToExcel = async (event) => {
    try {
      const XLSX = await import('xlsx');
      
      // Prepare data for Excel
      const data = formsStatus.map(form => ({
        'Form': form.form,
        'Teacher': form.teacher_name || '-',
        'Status': form.submitted ? 'Submitted' : 'Pending',
        'Present': form.present_count ?? 0,
        'Missing': form.missing_count ?? 0,
        'Time Spent': form.time_spent_seconds ? formatDuration(form.time_spent_seconds) : '-'
      }));

      // Create workbook with summary sheet
      const ws_data = [
        ['Fire Drill Report'],
        [],
        ['Date:', formatDate(event.created_at)],
        ['Started by:', event.started_by || 'System'],
        ['Duration:', calculateEventDuration(event) ? formatDuration(calculateEventDuration(event)) : 'N/A'],
        ['Reports Submitted:', `${formsStatus.filter(f => f.submitted).length} / ${formsStatus.length}`],
        [],
        ['Form Submissions']
      ];

      // Add headers
      const headers = Object.keys(data[0] || {});
      ws_data.push(headers);

      // Add data rows
      data.forEach(row => {
        ws_data.push(headers.map(h => row[h]));
      });

      // Create sheet
      const ws = XLSX.utils.aoa_to_sheet(ws_data);
      
      // Set column widths
      const colWidths = [15, 20, 12, 10, 10, 15];
      ws['!cols'] = colWidths.map(w => ({ wch: w }));

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');

      // Save file
      const fileName = `fire-drill-${formatDate(event.created_at).replace(/\s+/g, '_')}-${event.event_id.substring(0, 8)}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setSuccessMessage(`📊 Excel report downloaded: ${fileName}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export to Excel: ' + err.message);
    }
  };

  const exportToPDF = async (event) => {
    try {
      // Use pre-imported jsPDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 15;

      // Title
      doc.setFontSize(18);
      doc.setTextColor(147, 51, 234);
      doc.text('Fire Drill Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 12;

      // Summary section
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Date: ${formatDate(event.created_at)}`, 15, yPosition);
      yPosition += 7;
      doc.text(`Started by: ${event.started_by || 'System'}`, 15, yPosition);
      yPosition += 7;
      doc.text(`Duration: ${calculateEventDuration(event) ? formatDuration(calculateEventDuration(event)) : 'N/A'}`, 15, yPosition);
      yPosition += 7;
      doc.text(`Reports Submitted: ${formsStatus.filter(f => f.submitted).length} / ${formsStatus.length}`, 15, yPosition);
      yPosition += 15;

      // Table header
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(147, 51, 234);
      
      const colWidth = (pageWidth - 30) / 6;
      const headers = ['Form', 'Teacher', 'Status', 'Present', 'Missing', 'Time'];
      
      headers.forEach((header, i) => {
        doc.rect(15 + i * colWidth, yPosition - 5, colWidth, 7, 'F');
        doc.text(header, 15 + i * colWidth + 2, yPosition, { maxWidth: colWidth - 2 });
      });
      yPosition += 8;

      // Table rows
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      
      formsStatus.forEach((form, idx) => {
        // Alternate row color
        if (idx % 2 === 0) {
          doc.setFillColor(240, 240, 250);
          doc.rect(15, yPosition - 5, pageWidth - 30, 7, 'F');
        }

        const rowData = [
          form.form,
          form.teacher_name || '-',
          form.submitted ? 'Submitted' : 'Pending',
          String(form.present_count ?? 0),
          String(form.missing_count ?? 0),
          form.time_spent_seconds ? formatDuration(form.time_spent_seconds) : '-'
        ];

        rowData.forEach((cell, i) => {
          doc.text(String(cell), 15 + i * colWidth + 2, yPosition, { maxWidth: colWidth - 2 });
        });
        yPosition += 7;

        // Check if we need a new page
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 15;
        }
      });

      // Save PDF
      const fileName = `fire-drill-${formatDate(event.created_at).replace(/\s+/g, '_')}-${event.event_id.substring(0, 8)}.pdf`;
      doc.save(fileName);

      setSuccessMessage(`📄 PDF report downloaded: ${fileName}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error exporting to PDF:', err);
      setError('Failed to export to PDF. Please try the Excel format instead.');
    }
  };

  const startFireDrill = async () => {
    setStartingEvent(true);
    setError(null);
    setSuccessMessage('');
    try {
      const response = await fetch(buildApiUrl('/fire-drill/start-event'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          started_by: currentUser?.email || 'Unknown'
        })
      });

      const data = await response.json();
      if (data.success) {
        const startTime = Date.now();
        setCurrentEventId(data.event_id);
        setDrillStartTime(startTime);
        // Save to localStorage so it persists across navigation
        localStorage.setItem('activeFireDrillEventId', data.event_id);
        localStorage.setItem('firedrillStartTime', startTime.toString());
        
        // 🔥 FIX: Clear old data and load fresh current data
        setMissingStudents({});
        setReports({});
        setFormsStatus([]);
        setViewingEventDetails(false);
        
        setSuccessMessage(`✅ Fire drill event started! Event ID: ${data.event_id}`);
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // Load fresh events and data
        await loadEvents();
        await loadData();
      } else {
        setError('Failed to start fire drill: ' + data.error);
      }
    } catch (err) {
      console.error('Error starting fire drill:', err);
      setError('Failed to start fire drill: ' + err.message);
    } finally {
      setStartingEvent(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      
      // Handle ISO datetime format (2025-01-15T10:30:00Z or similar)
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        // Handle date-only format (YYYY-MM-DD)
        date = new Date(dateString + 'T00:00:00');
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString || 'N/A';
    }
  };

  const formatTime = (timeString) => {
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timeString;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-2xl">🚨</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold">Fire Marshal Dashboard</h1>
                <p className="text-purple-100 mt-1">Monitor all fire drill reports and missing students</p>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600">
                  Last updated: <span className="font-medium">{new Date().toLocaleTimeString()}</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">Auto-refreshes every 30 seconds</p>
                {currentEventId && (
                  <p className="text-sm text-green-600 mt-2 font-medium">
                    🟢 Active Event: {currentEventId.substring(0, 8)}...
                  </p>
                )}
                <p className="text-sm text-purple-600 mt-2 font-medium">
                  📚 Showing all school classes
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={startFireDrill}
                  disabled={startingEvent || currentEventId}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  title={currentEventId ? "A fire drill is already active. End it first." : ""}
                >
                  {startingEvent ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Starting...</span>
                    </>
                  ) : currentEventId ? (
                    <>
                      <span>🚨</span>
                      <span>Fire Drill Active</span>
                    </>
                  ) : (
                    <>
                      <span>🚨</span>
                      <span>Start Fire Drill</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-green-700">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            ⚠️ {error}
          </div>
        )}

        {/* Main View Tabs */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setMainView('active')}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                mainView === 'active'
                  ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              🚨 Active Fire Drill
            </button>
            <button
              onClick={() => {
                setMainView('previous');
                setLoading(true);
                loadEvents();
              }}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                mainView === 'previous'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              📊 Previous Drills
            </button>
          </div>
        </div>

        {/* Active Fire Drill Section */}
        {mainView === 'active' && (
        <>

        {/* Active Fire Drill Event Container - Thin */}
        {currentEventId && (
          <div 
            onClick={() => {
              // Don't toggle on container click anymore
            }}
            className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg shadow-lg overflow-hidden mb-6 cursor-pointer hover:shadow-xl transition-shadow"
          >
            <div className="px-6 py-3 text-white">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                {/* Event Status */}
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                    <span className="text-lg">🚨</span>
                  </div>
                  <div>
                    <p className="text-red-100 text-xs">ACTIVE</p>
                    <p className="text-sm font-bold">Fire Drill</p>
                  </div>
                </div>

                {/* Event Date */}
                <div>
                  <p className="text-red-100 text-xs">Date</p>
                  <p className="text-sm font-semibold">
                    {currentEvent?.created_at ? formatDate(currentEvent.created_at) : 'Today'}
                  </p>
                </div>

                {/* Started by */}
                <div>
                  <p className="text-red-100 text-xs">Started by</p>
                  <p className="text-sm font-semibold">
                    {currentEvent?.started_by || 'System'}
                  </p>
                </div>

                {/* Timer */}
                <div>
                  <p className="text-red-100 text-xs">Duration</p>
                  <p className="text-lg font-bold font-mono">{formatDuration(timer)}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // 🔥 FIX: Load fresh event details before showing
                      if (!viewingEventDetails && currentEventId) {
                        loadEventDetails(currentEventId);
                      }
                      setViewingEventDetails(!viewingEventDetails);
                    }}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 rounded text-white text-sm font-semibold transition-colors"
                  >
                    {viewingEventDetails ? '▼ Hide' : '▶ Show'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      endFireDrill();
                    }}
                    className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 rounded text-white text-sm font-semibold transition-colors"
                  >
                    🛑 End
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs - Only show when viewing active event details */}
        {viewingEventDetails && currentEventId && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="flex border-b border-gray-200 flex-wrap">
              <button
                onClick={() => setActiveTab('reports')}
                className={`flex-1 px-6 py-4 font-medium transition-colors min-w-max ${
                  activeTab === 'reports'
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                📋 Classes Report
                {filteredForms.length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold">
                    {filteredForms.length} forms
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('missing-students')}
                className={`flex-1 px-6 py-4 font-medium transition-colors min-w-max ${
                  activeTab === 'missing-students'
                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                👥 Missing Pupils
                {Object.keys(missingStudents).length > 0 && (
                  <span className="ml-2 px-2 py-1 bg-red-100 text-red-600 rounded-full text-sm font-semibold">
                    {Object.values(missingStudents).reduce((sum, arr) => sum + arr.length, 0)}
                  </span>
                )}
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* Classes Report Tab */}
              {activeTab === 'reports' && (
                <div className="space-y-4">
                  {filteredForms.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📋</div>
                      <h3 className="text-xl font-semibold text-gray-900">No Forms Data</h3>
                      <p className="text-gray-600 mt-2">Form data will appear here as reports are submitted</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-100 border-b-2 border-gray-300">
                          <tr>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Form name</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Total absent</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Teacher</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Submitted</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Time spent</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredForms.map((form) => (
                            <tr
                              key={form.form}
                              className={`hover:bg-gray-50 transition-colors ${
                                form.submitted ? 'bg-white' : 'bg-amber-50'
                              }`}
                            >
                              {/* Form name */}
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xl">
                                    {form.submitted ? '✅' : '⏳'}
                                  </span>
                                  <span className="font-semibold text-gray-900">{form.form}</span>
                                </div>
                              </td>
                              
                              {/* Total absent */}
                              <td className="px-6 py-4">
                                {form.submitted ? (
                                  <div className="flex items-center">
                                    <span className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full font-semibold text-sm">
                                      {form.missing_count}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-500 italic">Pending</span>
                                )}
                              </td>
                              
                              {/* Teacher */}
                              <td className="px-6 py-4">
                                {form.submitted ? (
                                  <span className="text-gray-900">{form.teacher_name || 'N/A'}</span>
                                ) : (
                                  <span className="text-gray-500 italic">—</span>
                                )}
                              </td>

                              {/* Submitted */}
                              <td className="px-6 py-4">
                                <span className={`inline-block px-3 py-1 rounded-full font-semibold text-sm ${
                                  form.submitted 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {form.submitted ? 'Yes' : 'No'}
                                </span>
                              </td>
                              
                              {/* Time spent */}
                              <td className="px-6 py-4">
                                {form.submitted && form.time_spent_seconds !== null && form.time_spent_seconds !== undefined ? (
                                  <span className="text-gray-900 font-mono text-sm">
                                    {formatDuration(form.time_spent_seconds)}
                                  </span>
                                ) : (
                                  <span className="text-gray-500 italic">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Missing Pupils Tab */}
              {activeTab === 'missing-students' && (
                <div className="space-y-6">
                  {Object.keys(missingStudents).length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">✅</div>
                      <h3 className="text-xl font-semibold text-gray-900">All Students Accounted For</h3>
                      <p className="text-gray-600 mt-2">No missing students in this fire drill</p>
                    </div>
                  ) : (
                    <>
                      {/* Missing Students Grouped by Form */}
                      {Object.entries(missingStudents)
                        .sort(([formA], [formB]) => formA.localeCompare(formB))
                        .map(([form, students]) => (
                          <div key={form} className="border border-red-200 rounded-lg overflow-hidden">
                            <div className="bg-red-50 px-6 py-4 border-b border-red-200">
                              <h3 className="text-lg font-semibold text-red-900">
                                {form}
                              </h3>
                              <p className="text-sm text-red-700 mt-1">
                                {students.length} student{students.length !== 1 ? 's' : ''} missing
                              </p>
                            </div>
                            
                            <div className="divide-y divide-red-100">
                              {students.map((student, idx) => (
                                <div key={idx} className="px-6 py-3 hover:bg-red-50 transition-colors">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                      <p className="text-sm text-gray-600">Student Name</p>
                                      <p className="font-medium text-gray-900">{student.student_name || student.student_id}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-600">Form / Class</p>
                                      <p className="font-medium text-gray-900">{student.form}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-gray-600">Teacher</p>
                                      <p className="font-medium text-gray-900">{student.teacher}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        </>
        )}

        {/* Previous Drills Section */}
        {mainView === 'previous' && (
          <div className="space-y-4">
            {!selectedCompletedEvent ? (
              // List of all completed events - show immediately without loading spinner
              <>
                {events.filter(e => e.status === 'completed').length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📊</div>
                    <h3 className="text-xl font-semibold text-gray-900">No Previous Drills</h3>
                    <p className="text-gray-600 mt-2">No completed fire drills yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {events
                      .filter(e => e.status === 'completed')
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((event) => (
                        <div
                          key={event.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-purple-300 transition-all"
                        >
                          <div
                            onClick={() => {
                              setSelectedCompletedEvent(event);
                              loadEventDetails(event.event_id);
                            }}
                            className="cursor-pointer"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                              <div>
                                <p className="text-xs text-gray-600 uppercase tracking-wide">Date</p>
                                <p className="font-semibold text-gray-900">
                                  {formatDate(event.created_at)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 uppercase tracking-wide">Started by</p>
                                <p className="font-semibold text-gray-900">
                                  {event.started_by || 'System'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 uppercase tracking-wide">Duration</p>
                                <p className="font-semibold text-gray-900">
                                  {calculateEventDuration(event) ? formatDuration(calculateEventDuration(event)) : 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 uppercase tracking-wide">Reports</p>
                                <p className="text-xl font-bold text-purple-600">
                                  {event.report_count || 0}
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 flex items-center text-sm text-purple-600">
                              <span>View details</span>
                              <span className="ml-2">→</span>
                            </div>
                          </div>
                          <div className="mt-3 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCompletedEvent(event);
                                  loadEventDetails(event.event_id).then(() => {
                                    exportToPDF(event);
                                  });
                                }}
                                className="px-3 py-2 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                                title="Download as PDF"
                              >
                                📄 PDF
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCompletedEvent(event);
                                  loadEventDetails(event.event_id).then(() => {
                                    exportToExcel(event);
                                  });
                                }}
                                className="px-3 py-2 text-sm bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors"
                                title="Download as Excel"
                              >
                                📊 Excel
                              </button>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(`Delete this fire drill and all its reports? This cannot be undone.`)) {
                                  deleteFireDrillEvent(event.event_id);
                                }
                              }}
                              className="px-3 py-2 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </>
            ) : (
              // Details of selected completed event
              loadingDetails ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-600 font-medium">Loading event details...</p>
                </div>
              ) : (
              <div className="space-y-6">
                <button
                  onClick={() => {
                    setSelectedCompletedEvent(null);
                    setSelectedCompletedEventDetails(null);
                  }}
                  className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-2"
                >
                  ← Back to Previous Drills
                </button>

                {/* Event Summary */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-gray-600 uppercase tracking-wide">Date</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatDate(selectedCompletedEvent.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 uppercase tracking-wide">Total Duration</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {calculateEventDuration(selectedCompletedEvent) ? formatDuration(calculateEventDuration(selectedCompletedEvent)) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 uppercase tracking-wide">Reports Submitted</p>
                      <p className="text-2xl font-bold text-green-600">
                        {selectedCompletedEvent.report_count || 0} forms
                      </p>
                    </div>
                  </div>
                </div>

                {/* Forms Table */}
                {filteredForms.length > 0 ? (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Form</th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Teacher</th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                          <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">Present</th>
                          <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">Missing</th>
                          <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">Time Spent</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredForms.map((form) => (
                          <tr key={form.form} className="hover:bg-gray-50">
                            <td className="px-6 py-3 font-medium text-gray-900">{form.form}</td>
                            <td className="px-6 py-3 text-gray-700">{form.teacher_name || '—'}</td>
                            <td className="px-6 py-3">
                              {form.submitted ? (
                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                  ✅ Submitted
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                                  ⏳ Pending
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-3 text-center font-mono text-sm text-gray-700">
                              {form.present_count ?? '0'}
                            </td>
                            <td className="px-6 py-3 text-center font-mono text-sm">
                              {form.missing_count > 0 ? (
                                <span className="text-red-600 font-bold">{form.missing_count}</span>
                              ) : (
                                <span className="text-green-600">0</span>
                              )}
                            </td>
                            <td className="px-6 py-3 text-center font-mono text-sm text-gray-700">
                              {form.time_spent_seconds ? (
                                <span className="text-gray-900 font-semibold">
                                  {formatDuration(form.time_spent_seconds)}
                                </span>
                              ) : (
                                <span className="text-gray-500 italic">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-600">
                    No form data available for this drill
                  </div>
                )}
              </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FireMarshalDashboard;
